import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { loadUser, user } from '../../store/reducers/user';
import { TimerCountUp } from '../../controls/agyle/timer';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import config from '../../app/config';
import clsx from 'clsx';
import ErrorIcon from '@mui/icons-material/Error';
import AgyleButton from '../ui/buttons/agyleButton';

const WIDTH = 600;

const useStyles = makeStyles((theme) => ({
    timerCountdownBlock: {
        background: '#f4c3c6',
        border: 'solid 2px #70191c',
        textAlign: 'center',
        marginLeft: `calc(50% - ${WIDTH / 2}px)`,
        top: 50,
        position: 'absolute',
        zIndex: 99999,
        padding: 12,
        borderRadius: 8,
        color: '#70191c',
        fontWeight: 'bold',
        width: WIDTH,
    },
    infoBlock: {
        display: 'inline-block',
        paddingLeft: '3px',
        paddingRight: '3px',
    },
    infoTimer: {
        fontSize: '18px',
        fontWeight: 600,
    },
    button: {
        color: '#70191c',
        borderColor: '#70191c',
        '&:hover': {
            backgroundColor: '#E25B61',
            borderColor: '#E25B61',
        },
    },
    fontWeight: {
        fontWeight: 'bold',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
    },
}));

function SessionTimer(props) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const userObject = useSelector(user);
    const [duration, setDuration] = React.useState(30);
    const [showInfoMinutesBefore, setShowInfoMinutesBefore] = React.useState(10);
    const [openModal, setOpenModal] = React.useState(false);
    const mobileDefaultTimeout = window.envConfig.timeout.mobileDefaultTimeout;
    const mobileMinutesBeforeTimeoutToShowInfo = window.envConfig.timeout.mobileMinutesBeforeTimeoutToShowInfo;
    const regularDefaultTimeout = window.envConfig.timeout.regularDefaultTimeout;
    const regularMinutesBeforeTimeoutToShowInfo = window.envConfig.timeout.regularMinutesBeforeTimeoutToShowInfo;
    const UsersForTimeoutOverwrite = window.envConfig.timeout.UsersForTimeoutOverwrite;
    const OverwriteTimeoutDuration = window.envConfig.timeout.OverwriteTimeoutDuration;
    const countDownDate = React.useRef(moment().add(duration, 'minutes'));
    const authObject = window.keycloak ? window.keycloak : window.auth;

    const setBasicFactors = () => {
        if (isMobile) {
            setDuration(mobileDefaultTimeout);
            setShowInfoMinutesBefore(mobileMinutesBeforeTimeoutToShowInfo);
        } else {
            setDuration(regularDefaultTimeout);
            setShowInfoMinutesBefore(regularMinutesBeforeTimeoutToShowInfo);
        }
    };

    const handleClickEvent = () => {
        window._lastClick = moment().valueOf();
    };

    const refreshApiSession = () => {
        dispatch(loadUser(false));
        setOpenModal(false);
        //use iframe window to post message and refresh token !!!!
    };

    // Init Event and Basic Factors
    useEffect(() => {
        setBasicFactors();

        // //change duration if username in special array
        // if (UsersForTimeoutOverwrite.indexOf(userObject.userName) > -1) {
        //     setDuration(OverwriteTimeoutDuration);
        // }

        // First click log
        window._lastClick = moment().valueOf();

        window.addEventListener('click', handleClickEvent);
        return () => {
            window.removeEventListener('click', handleClickEvent);
        };
    }, []);

    useEffect(() => {
        //change duration if username in special array
        if (UsersForTimeoutOverwrite.indexOf(userObject.userName) > -1) {
            setDuration(OverwriteTimeoutDuration);
        }
    }, [userObject]);

    useEffect(() => {
        let date = moment().add(5, 'minute');
        let refreshCounter = config?.timeout?.refreshCounter || 20;

        const iid = setInterval(() => {
            // Frequently verified if token is valid

            if (!config?.debugNoTokenAuth && refreshCounter > 0) {
                authObject
                    .updateToken(60)
                    .then((refreshed) => {
                        if (refreshed) {
                            refreshCounter = config?.timeout?.refreshCounter || 20;
                            // console.log('Token was successfully refreshed');
                        } else {
                            // console.log('Token is still valid');
                        }
                    })
                    .catch(function () {
                        refreshCounter--;
                        console.error('Failed to refresh the token, or the session has expired');
                        authObject.logout();
                    });
            }

            // Check last click to open modal
            if (moment().diff(moment(window._lastClick), 'second') >= (duration - showInfoMinutesBefore) * 60) {
                const timeoutTimestamp = moment(window._lastClick).add(duration, 'minutes');
                // if(moment().diff(timeoutTimestamp) > 0) {
                //     console.log("need logout now!!!", moment().diff(timeoutTimestamp), timeoutTimestamp.format('HH:mm:ss'), moment().format("HH:mm:ss"));
                //     //authObject.logout();
                // }
                if (openModal === false) {
                    countDownDate.current = moment(window._lastClick).add(duration, 'minutes');
                    setOpenModal(true);
                }
            }

            if (moment().isAfter(date)) {
                date = moment().add(5, 'minute');
                refreshApiSession();
            }
        }, 1000);

        return () => {
            clearInterval(iid);
        };
    }, [userObject.userName, showInfoMinutesBefore, openModal, duration]);

    const countdownLimitCatch = () => {
        authObject.logout();
    };

    if (!openModal) return null;

    return (
        <div className={clsx(classes.timerCountdownBlock, classes.divRow)}>
            <ErrorIcon />

            <div className={classes.infoBlock}>{t('Your Session will expire in')}</div>

            <div className={clsx(classes.infoBlock, classes.infoTimer)}>
                <TimerCountUp
                    time={countDownDate.current}
                    catchLimit={countdownLimitCatch}
                    limitMessage={t('Logout is in progress')}
                    countDown
                />
            </div>

            <AgyleButton className={classes.button} fontClassName={classes.fontWeight} onClick={() => authObject.logout()} variant="text">
                {t('Log out')}
            </AgyleButton>

            <AgyleButton className={classes.button} fontClassName={classes.fontWeight} onClick={refreshApiSession} variant="outlined">
                {t('Renew Session')}
            </AgyleButton>
        </div>
    );
}

export { SessionTimer };
